import { memo, useCallback, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { Button, Table, Form, Radio, Space, Tooltip, message, Typography } from 'antd';
import { JustRightHeightModal } from 'components/styled/scoped';
import ItemContent from './Content';
import { css } from '@emotion/css';
import { EditEstimatedAvailableTime, useCancelMark } from 'views/Aide/OutStock/Operate';
import { QuestionCircleOutlined } from '@ant-design/icons';
import StatusTag from './StatusTag';
import EstimatedAvailableTimeTip from './EstimatedAvailableTimeTip';
import { addStockRule } from 'views/Aide/OutStock/api';
import dayjs, { Dayjs } from 'lib/dayjs';
import { filter, map, flatten } from 'lodash-es';
import { useIsStoreAliasFirst } from 'hooks/business/store/useStoreName';
import { DatePicker } from 'components/DatePicker';
import { useUpdateProduct } from '../useProductV2';

type SkuMarkStockProps = {
  visible: boolean;
  onCancel: () => void;
  product: Conversation.Product;
  conversation: Conversation.Conversation;
};

/**
 * 当有sku的时候，弹出的弹窗
 */
const SkuMarkStock = memo((props: SkuMarkStockProps) => {
  const { visible, onCancel, product, conversation } = props;
  const { t } = useTranslate();
  const { skuInfo } = product;
  const [openToMark, instance] = useMark(product, conversation);
  const [rowKeys, setRowKeys] = useState<string[]>([]);
  const cancelMark = useCancelMark();
  const updateProduct = useUpdateProduct();

  return (
    <JustRightHeightModal
      title={t('scene.chatWorkspace.productTab.inventory.action.markOutOfStock')}
      width={940}
      onCancel={onCancel}
      visible={visible}
      className={css`
        .ant-modal-body {
          padding: 17px 20px !important;
          margin: 0 !important;
        }
      `}
      footer={null}
      centered
    >
      <div>
        {instance}
        <ItemContent product={product} conversation={conversation} />
        <div className="my-[12px] h-[20px] flex items-center">
          <div className="leading-[20px] text-[#84868c]">
            {t('common.label.totalItems', { x: skuInfo.length })}
          </div>
          {rowKeys.length > 0 && (
            <>
              <Button
                onClick={() => {
                  openToMark(
                    filter(skuInfo, (sku) => rowKeys.includes(sku.skuId)),
                    () => {
                      setRowKeys([]);
                    },
                  );
                }}
                type="link"
                className="!px-0 ml-[16px]"
              >
                {t('scene.chatWorkspace.productTab.inventory.action.batchMarkOutOfStock')}
              </Button>
              <Button
                onClick={() => {
                  cancelMark(
                    flatten(
                      map(
                        filter(skuInfo, (sku) => rowKeys.includes(sku.skuId)),
                        (e) => e.stockRuleId,
                      ),
                    ),
                    () => {
                      setRowKeys([]);

                      updateProduct(
                        product.externalItemId,
                        product.internalStoreId,
                        conversation.externalBuyerId,
                      );
                    },
                  );
                }}
                type="link"
                className="!px-0 ml-[16px]"
              >
                {t('scene.chatWorkspace.productTab.inventory.action.batchUnmarkOutOfStock')}
              </Button>
            </>
          )}
        </div>
        <Table
          dataSource={skuInfo}
          pagination={
            skuInfo?.length > 10
              ? {
                  pageSize: 10,
                  total: skuInfo?.length,
                }
              : false
          }
          rowSelection={{
            selectedRowKeys: rowKeys,
            onChange: (selectedRowKeys) => {
              setRowKeys(selectedRowKeys as string[]);
            },
          }}
          rowKey="skuId"
          columns={[
            {
              title: 'SKU',
              dataIndex: 'skuName',
              key: 'skuName',
              width: 200,
              render: (value) => {
                return (
                  <Typography.Paragraph
                    ellipsis={{ tooltip: true, rows: 2 }}
                    style={{ wordBreak: 'break-all' }}
                  >
                    {value || '-'}
                  </Typography.Paragraph>
                );
              },
            },
            {
              title: t('domain.product.field.skuCode'),
              dataIndex: 'sellerSku',
              key: 'sellerSku',
              width: 200,
              render: (value) => {
                return (
                  <Typography.Paragraph
                    ellipsis={{ tooltip: true, rows: 2 }}
                    style={{ wordBreak: 'break-all' }}
                  >
                    {value || '-'}
                  </Typography.Paragraph>
                );
              },
            },
            {
              title: (
                <div>
                  <span className="mr-[4px]">
                    {t('scene.chatWorkspace.productTab.inventory.label.stockStatus')}
                  </span>
                  <Tooltip
                    title={
                      <div>
                        <div>
                          {t('scene.chatWorkspace.productTab.inventory.hint.platformStockStatus')}
                        </div>
                        <div>
                          {t(
                            'scene.chatWorkspace.productTab.inventory.hint.manuallyMarkedDescription',
                          )}
                        </div>
                      </div>
                    }
                  >
                    <QuestionCircleOutlined />
                  </Tooltip>
                </div>
              ),
              dataIndex: 'skuStockStatus',
              key: 'skuStockStatus',
              render: (skuStockStatus) => {
                return <StatusTag status={skuStockStatus} />;
              },
            },
            {
              title: (
                <div>
                  <span className="mr-[4px]">
                    {t('scene.chatWorkspace.productTab.inventory.label.restockDate')}
                  </span>
                  <EstimatedAvailableTimeTip />
                </div>
              ),
              dataIndex: 'estimatedAvailableTime',
              key: 'estimatedAvailableTime',
              render: (value, record) => {
                return (
                  <EditEstimatedAvailableTime
                    noEdit={record.skuStockStatus !== 'MarkedOutOfStock'}
                    value={value}
                    id={record.stockRuleId}
                    onOk={() => {
                      updateProduct(
                        product.externalItemId,
                        product.internalStoreId,
                        conversation.externalBuyerId,
                      );
                    }}
                  />
                );
              },
            },
            {
              title: t('common.action.operation'),
              key: 'action',
              render: (_, record) => {
                if (record.skuStockStatus === 'MarkedOutOfStock') {
                  return (
                    <Button
                      className="!px-0"
                      onClick={() => {
                        cancelMark(record.stockRuleId, () => {
                          updateProduct(
                            product.externalItemId,
                            product.internalStoreId,
                            conversation.externalBuyerId,
                          );
                        });
                      }}
                      type="link"
                    >
                      {t('scene.chatWorkspace.productTab.inventory.action.unmarkOutOfStock')}
                    </Button>
                  );
                }
                return (
                  <Button
                    className="!px-0"
                    onClick={() => {
                      openToMark([record]);
                    }}
                    type="link"
                  >
                    {t('scene.chatWorkspace.productTab.inventory.action.markOutOfStock')}
                  </Button>
                );
              },
            },
          ]}
        ></Table>
      </div>
    </JustRightHeightModal>
  );
});

export const useSkuMarkStock = (
  product: Conversation.Product,
  conversation: Conversation.Conversation,
) => {
  const [visible, setVisible] = useState(false);
  const open = useCallback(() => {
    setVisible(true);
  }, []);

  const close = useCallback(() => {
    setVisible(false);
  }, []);

  const instance = visible && (
    <SkuMarkStock
      product={product}
      conversation={conversation}
      visible={visible}
      onCancel={close}
    />
  );
  return [open, instance] as const;
};

/**
 * 当没有sku的时候，且人工标记的时候，弹出的弹窗
 */
const NonSkusMarkStock = memo((props: SkuMarkStockProps) => {
  const { visible, onCancel, product, conversation } = props;
  const { t } = useTranslate();
  const cancelMark = useCancelMark();
  const updateProduct = useUpdateProduct();

  return (
    <JustRightHeightModal
      title={t('scene.chatWorkspace.productTab.inventory.action.markOutOfStock')}
      width={940}
      onCancel={onCancel}
      visible={visible}
      className={css`
        .ant-modal-body {
          padding: 17px 20px !important;
          margin: 0 !important;
        }
      `}
      footer={
        <div>
          <Button
            onClick={() => {
              cancelMark(product.stockRuleId, () => {
                updateProduct(
                  product.externalItemId,
                  product.internalStoreId,
                  conversation.externalBuyerId,
                );
                onCancel();
              });
            }}
            type="primary"
          >
            {t('scene.chatWorkspace.productTab.inventory.action.unmarkOutOfStock')}
          </Button>
        </div>
      }
    >
      <div>
        <ItemContent product={product} conversation={conversation} />
        <div className="h-[24px] items-center flex mt-[16px] text-[#232426]">
          <div className="mr-[6px]">
            {t('scene.chatWorkspace.productTab.inventory.label.stockStatus')}:
          </div>
          <div className="inline-block leading-[18px] px-[8px] py-[3px] text-[12px] text-[#f05b5b] bg-[#fff0f0] rounded-[2px]">
            {t('domain.product.inventoryStatus.manuallyMarkedOutOfStock')}
          </div>
        </div>
        <div className="h-[20px] items-center flex mt-[12px] text-[#232426]">
          <div className="inline-flex items-center flex-shrink-0 mr-[6px]">
            <span className="mr-[4px]">
              {t('scene.chatWorkspace.productTab.inventory.label.restockDate')}
            </span>
            <EstimatedAvailableTimeTip />
            &nbsp;:
          </div>

          <EditEstimatedAvailableTime
            value={product.estimatedAvailableTime}
            id={product.stockRuleId}
            onOk={() => {
              updateProduct(
                product.externalItemId,
                product.internalStoreId,
                conversation.externalBuyerId,
              );
            }}
          />
        </div>
      </div>
    </JustRightHeightModal>
  );
});

export const useNonSkuMarkStock = (
  product: Conversation.Product,
  conversation: Conversation.Conversation,
) => {
  const [visible, setVisible] = useState(false);
  const open = useCallback(() => {
    setVisible(true);
  }, []);

  const close = useCallback(() => {
    setVisible(false);
  }, []);

  const instance = visible && (
    <NonSkusMarkStock
      product={product}
      conversation={conversation}
      visible={visible}
      onCancel={close}
    />
  );
  return [open, instance] as const;
};

type MarkProps = {
  visible: boolean;
  onCancel: () => void;
  product: Conversation.Product;
  conversation: Conversation.Conversation;
  skus: Conversation.Product['skuInfo'];
  noScope?: boolean;
};

const Mark = memo((props: MarkProps) => {
  const { visible, onCancel, product, conversation, skus, noScope = false } = props;
  const { t } = useTranslate();
  const [form] = Form.useForm<{ scope: number; estimatedAvailableTime: Dayjs }>();
  const updateProduct = useUpdateProduct();

  const isStoreAliasFirst = useIsStoreAliasFirst(conversation.channel, conversation.region);
  return (
    <JustRightHeightModal
      title={t('scene.chatWorkspace.productTab.inventory.action.markOutOfStock')}
      width={484}
      visible={visible}
      onCancel={onCancel}
      onOk={() => {
        const { scope, estimatedAvailableTime } = form.getFieldsValue();
        const templateType = scope === 1 ? 2 : scope === 2 ? 3 : 1;
        addStockRule({
          stockRuleData: {
            templateType,
            spuId: product.externalItemId,
            skuId: skus.map((sku) => sku.skuId),
            sellerSku: skus.map((sku) => sku.sellerSku),
            externalType: conversation.channel,
            region: conversation.region,
            estimatedAvailableTime: estimatedAvailableTime
              ? estimatedAvailableTime.format('YYYY-MM-DD')
              : '',
          },
        })
          .then(() => {
            message.success(t('domain.product.inventoryStatus.markedOutOfStock'));
            onCancel();
            updateProduct(
              product.externalItemId,
              product.internalStoreId,
              conversation.externalBuyerId,
            );
          })
          .catch(() => {
            message.error(t('common.feedback.retryLater'));
          });
      }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          scope: 1,
        }}
      >
        {!noScope && (
          <Form.Item
            label={`${t('scene.chatWorkspace.productTab.inventory.scope.choose')}:`}
            required
            name="scope"
          >
            <Radio.Group>
              <Space direction="vertical">
                <Radio value={1}>
                  {t('scene.chatWorkspace.productTab.inventory.scope.currentStore')}(
                  {isStoreAliasFirst
                    ? conversation.storeAlias || conversation.storeName
                    : conversation.storeName}
                  )
                </Radio>
                <Radio value={2}>
                  {t('scene.chatWorkspace.productTab.inventory.scope.allAuthorizedStores')}
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
        )}
        <Form.Item
          label={
            <div>
              {t('scene.chatWorkspace.productTab.inventory.label.restockDate')}
              <span className="px-[4px]">
                <EstimatedAvailableTimeTip />
              </span>
              :
            </div>
          }
          name="estimatedAvailableTime"
        >
          <DatePicker
            allowClear
            style={{ width: '238px' }}
            showToday={false}
            disabledDate={(current) => {
              return current && current < dayjs();
            }}
          />
        </Form.Item>
      </Form>
    </JustRightHeightModal>
  );
});

export const useMark = (product: Conversation.Product, conversation: Conversation.Conversation) => {
  const skusRef = useRef<Conversation.Product['skuInfo']>([]);
  const [visible, setVisible] = useState(false);
  const callbackRef = useRef<() => void>();
  const open = useCallback((skus?: Conversation.Product['skuInfo'], callback?: () => void) => {
    setVisible(true);
    skusRef.current = skus || [];
    callbackRef.current = callback;
  }, []);

  const close = useCallback(() => {
    setVisible(false);
    callbackRef.current?.();
  }, []);

  const instance = visible && (
    <Mark
      visible={visible}
      onCancel={close}
      product={product}
      conversation={conversation}
      skus={skusRef.current}
      noScope={skusRef.current?.length === 0}
    />
  );
  return [open, instance] as const;
};
